import { useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useCallback, useContext, useEffect, useRef } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { Box, Heading, Flex } from "@chakra-ui/layout"

import ShowCard from "./show-card"
import { HIDE_GRID_TITLE, Views } from "../../../constants"
import { SDKContext } from "../../../context/SDKContext"
import { viewSelector } from "../../../recoil/atoms/view"
import { useValidateRoom } from "../../../hooks/useValidateRoom"
import {
  isShowLoadingAtom,
  loadingRoomHashAtom,
  navigatedFromShowListAtom,
  showsErrorMessageAtom,
  showsSelector
} from "../../../recoil/atoms/shows"
import LoadingView from "../loading"
import MOLocation from "../../../util/MOLocation"
import ShowsErrorMessage from "./shows-error-message"
import ShowCardSkeleton from "./show-card-skeleton"

const Shows = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const meetingClient = useContext(SDKContext)
  const { validateRoom } = useValidateRoom(meetingClient)

  const [view, setView] = useRecoilState(viewSelector)

  const isShowLoading = useRecoilValue(isShowLoadingAtom)
  const showsErrorMessage = useRecoilValue(showsErrorMessageAtom)
  const setNavigatedFromShowList = useSetRecoilState(navigatedFromShowListAtom)
  const { rooms: meetingRoomList, ...showRoomBranding } =
    useRecoilValue(showsSelector)

  const selectedRoomHash = useRef(null)
  const [loadingRoomhash, setLoadingRoomhash] =
    useRecoilState(loadingRoomHashAtom)

  /** Navigate to provide roomhash and clear the cache */
  const navigateToRoomHash = (roomhash: string) => {
    history.push(roomhash)
    selectedRoomHash.current = null
    setLoadingRoomhash(null)
  }

  /** This useEffect is use to navigate to roomhash, after the meeting is joined */
  useEffect(() => {
    if (view === Views.MEETING_ROOM && selectedRoomHash.current) {
      navigateToRoomHash(selectedRoomHash.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  /** Connect the websocket when component is loaded */
  useEffect(() => {
    if (meetingClient) {
      meetingClient?.connectShows()
    }
  }, [meetingClient])

  /** Handle on click on show card. This function open the given room hash meeting */
  const handleOpenShow = async (roomHash: string) => {
    // Disconnect the previous meeting if active
    await meetingClient?.disconnectGateway(0)

    meetingClient.setRoomHash(roomHash)
    setLoadingRoomhash(roomHash)
    selectedRoomHash.current = roomHash

    // If the user is inside the meeting, then navigate directly by displaying fullscreen loader
    if (view === Views.MEETING_ROOM) {
      setView(Views.LOADING)
      navigateToRoomHash(roomHash)
    }

    // Directly join the room if user has already join the room before, to check that we are using username.
    // If user name is present we are joining the room directly
    const joinRoom = !!meetingClient?.getUserName()
    await validateRoom({ joinRoomDirectly: joinRoom })

    // If we are not join room direct then navigate to home page, So that user can set userName
    if (!joinRoom) {
      navigateToRoomHash(roomHash)
      setView(Views.HOME)
    }

    // set true so that on leave we can redirect user to show screen
    setNavigatedFromShowList(true)
  }

  /** Open show on new tab with same settings */
  const openShowInNewTab = useCallback(
    (roomHash: string) => {
      // Create query param if userName is exist
      const userName = meetingClient?.getUserName()
      const queryParam = userName ? `?userName=${userName}` : ""

      // Generate the room url
      const showURL =
        MOLocation.getLocation().origin + `/${roomHash}${queryParam}`

      // Open url on new tab
      window.open(showURL, "_blank")
    },
    [meetingClient]
  )

  // If error occur, display error message
  if (showsErrorMessage) return <ShowsErrorMessage {...showsErrorMessage} />
  if (isShowLoading) return <LoadingView />

  return (
    <Box
      width="full"
      height="full"
      backgroundColor={
        showRoomBranding?.gridBackgroundColor
          ? showRoomBranding?.gridBackgroundColor
          : "gray.100"
      }
      backgroundImage={
        showRoomBranding?.gridBackgroundImage
          ? `url(${showRoomBranding?.gridBackgroundImage})`
          : "none"
      }
      backgroundSize={"cover"}
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      overflowY="auto"
    >
      <Box
        width={["94%", "86%"]}
        marginX="auto"
        height="full"
        paddingBottom="24px"
        maxWidth="1000px"
      >
        {!HIDE_GRID_TITLE && (
          <Heading
            textAlign="center"
            paddingY={"6"}
            color={showRoomBranding?.gridTitleColor}
          >
            {t("Shows")}
          </Heading>
        )}

        <Flex
          justifyContent="center"
          flexWrap="wrap"
          gridGap={["8px", "12px", "12px"]}
          paddingBottom="24px"
          alignContent="flex-start"
          paddingTop={HIDE_GRID_TITLE && "8"}
        >
          {meetingRoomList.map(room => (
            <ShowCard
              key={`show-list-${room.id}`}
              {...room}
              openShow={handleOpenShow}
              openShowInNewTab={openShowInNewTab}
              loadingRoomhash={loadingRoomhash}
              listLength={meetingRoomList.length}
            />
          ))}

          {meetingRoomList.length === 0 &&
            Array(4)
              .fill(0)
              .map((_, i) => (
                <ShowCardSkeleton
                  key={`show-card-skeleton-${i}`}
                  listLength={meetingRoomList.length}
                />
              ))}
        </Flex>

        {
          // <Box width={containerWidth}  marginX="auto">
          // <SimpleGrid
          //   spacingX={["8px", "10px", "12px"]}
          //   spacingY={["10px", "12px", "14px"]}
          //   templateColumns={[
          //     `repeat(${noOfColumn}, minmax(160px, 0.8fr))`,
          //     `repeat(${noOfColumn}, minmax(200px, 0.8fr))`,
          //     `repeat(${noOfColumn}, minmax(240px, 240px))`
          //   ]}
          //   alignItems="center"
          //   justifyContent="center"
          //   maxWidth="1000px" /* Maximum container width */
          //   margin="0 auto" /* Center the container horizontally */
          // >
          //   {/* Display Show Cards */}
          //   {meetingRoomList.map(room => (
          //     <ShowCard
          //       key={`show-list-${room.id}`}
          //       {...room}
          //       openShow={handleOpenShow}
          //       openShowInNewTab={openShowInNewTab}
          //       loadingRoomhash={loadingRoomhash}
          //     />
          //   ))}
          //   {/* Show Skeleton while loading */}
          //   {meetingRoomList.length === 0 &&
          //     Array(5)
          //       .fill(0)
          //       .map((_, i) => (
          //         <ShowCardSkeleton key={`show-card-skeleton-${i}`} />
          //       ))}
          // </SimpleGrid>
          // </Box>
        }
      </Box>
    </Box>
  )
}

export default Shows
